import { fetchSystemInfo, getNotificationList } from '/@/api/sys/system';
import type {
  ProjectConfig,
  HeaderSetting,
  MenuSetting,
  TransitionSetting,
  MultiTabsSetting,
} from '/#/config';
import type { BeforeMiniState } from '/#/store';

import { defineStore } from 'pinia';
import { store } from '/@/store';

import { ThemeEnum } from '/@/enums/appEnum';
import { APP_DARK_MODE_KEY_, PROJ_CFG_KEY } from '/@/enums/cacheEnum';
import { Persistent } from '/@/utils/cache/persistent';
import { darkMode } from '/@/settings/designSetting';
import { resetRouter } from '/@/router';
import { deepMerge } from '/@/utils';

import { usePermission } from '/@/hooks/web/usePermission';
import { MenuCodeEnum } from '/@/enums/menuCodeEnum';
import { useUserStore } from '/@/store/modules/user';

import {
  SystemInfo,
  NotificationParams,
  NotificationItem,
  IMsgNotificationItem,
  IFileNotificationItem,
} from '/@/api/sys/model/systemModel';
import { NotificationTypeEnum } from '/@/enums/sysEnum';

interface AppState {
  darkMode?: ThemeEnum;
  // Page loading status
  pageLoading: boolean;
  // project config
  projectConfig: ProjectConfig | null;
  // When the window shrinks, remember some states, and restore these states when the window is restored
  beforeMiniInfo: BeforeMiniState;
  // 检查当前时间
  checkTime: boolean;
  // 维护中
  maintenance: boolean;
  systemInfo: Nullable<SystemInfo>;

  // 是否有通知
  sysNotifyFlag: boolean;
  msgNotifyList: IMsgNotificationItem[];
  fileNotifyList: IFileNotificationItem[];
  allNotifyList: NotificationItem[];
}
let timeId: TimeoutHandle;
export const useAppStore = defineStore({
  id: 'app',
  state: (): AppState => ({
    darkMode: undefined,
    pageLoading: false,
    projectConfig: Persistent.getLocal(PROJ_CFG_KEY),
    beforeMiniInfo: {},
    // 检查当前时间
    checkTime: false,
    // 维护中
    maintenance: false,
    systemInfo: null,
    sysNotifyFlag: false,
    msgNotifyList: [],
    fileNotifyList: [],
    allNotifyList: [],
  }),
  getters: {
    getPageLoading(): boolean {
      return this.pageLoading;
    },
    getDarkMode(): 'light' | 'dark' | string {
      return this.darkMode || localStorage.getItem(APP_DARK_MODE_KEY_) || darkMode;
    },

    getBeforeMiniInfo(): BeforeMiniState {
      return this.beforeMiniInfo;
    },
    getProjectConfig(): ProjectConfig {
      return this.projectConfig || ({} as ProjectConfig);
    },
    getHeaderSetting(): HeaderSetting {
      return this.getProjectConfig.headerSetting;
    },
    getMenuSetting(): MenuSetting {
      return this.getProjectConfig.menuSetting;
    },
    getTransitionSetting(): TransitionSetting {
      return this.getProjectConfig.transitionSetting;
    },
    getMultiTabsSetting(): MultiTabsSetting {
      return this.getProjectConfig.multiTabsSetting;
    },
    getMaintenance(): boolean {
      return this.maintenance;
    },
    getSystemInfo(): Nullable<SystemInfo> {
      return this.systemInfo;
    },
    getSysNotifyFlag(): boolean {
      return this.sysNotifyFlag;
    },
    getAllSysNotifyList(): NotificationItem[] {
      return this.allNotifyList;
    },
    getNotificationIsShow(): boolean {
      const userStore = useUserStore();
      const { hasPermission } = usePermission();
      return !!userStore.getToken && !!hasPermission(MenuCodeEnum.sysNotification);
    },
  },
  actions: {
    setCheckTime(): void {
      this.checkTime = !this.checkTime;
    },
    setPageLoading(loading: boolean): void {
      this.pageLoading = loading;
    },

    setDarkMode(mode: ThemeEnum): void {
      this.darkMode = mode;
      localStorage.setItem(APP_DARK_MODE_KEY_, mode);
    },

    setBeforeMiniInfo(state: BeforeMiniState): void {
      this.beforeMiniInfo = state;
    },

    setProjectConfig(config: DeepPartial<ProjectConfig>): void {
      this.projectConfig = deepMerge(this.projectConfig || {}, config);
      Persistent.setLocal(PROJ_CFG_KEY, this.projectConfig);
    },

    async resetAllState() {
      resetRouter();
      Persistent.clearAll(true);
    },
    async setPageLoadingAction(loading: boolean): Promise<void> {
      if (loading) {
        clearTimeout(timeId);
        // Prevent flicker
        timeId = setTimeout(() => {
          this.setPageLoading(loading);
        }, 50);
      } else {
        this.setPageLoading(loading);
        clearTimeout(timeId);
      }
    },
    setMaintenance(maintenance: boolean): void {
      this.maintenance = maintenance;
    },
    async fetchSystemInfo() {
      const res = await fetchSystemInfo();
      this.systemInfo = res;
      return res;
    },
    setSysNotifyFlag(flag: boolean): void {
      this.sysNotifyFlag = flag;
    },
    async queryNotifyListByType(params: NotificationParams) {
      const res = await getNotificationList(params);
      if (typeof params.type === 'undefined') {
        this.allNotifyList = res.list;
        return {
          list: this.allNotifyList,
          total: res.total,
          currentPage: res.currentPage,
        };
      }
      if (params.type === NotificationTypeEnum.MSG_LIST) {
        this.msgNotifyList = res.list.map((item: NotificationItem) => {
          const { id, type, status, hasRead, notificationInfo } = item;
          return { type, status, hasRead, ...notificationInfo, id } as IMsgNotificationItem;
        });
        return {
          list: this.msgNotifyList,
          total: res.total,
          currentPage: res.currentPage,
        };
      }

      this.fileNotifyList = res.list.map((item: NotificationItem) => {
        const { id, type, status, hasRead, exportRecord } = item;
        return { type, status, hasRead, ...exportRecord, id } as IFileNotificationItem;
      });
      return {
        list: this.fileNotifyList,
        total: res.total,
        currentPage: res.currentPage,
      };
    },
    async queryNewNotifyList(): Promise<void> {
      const { list } = await this.queryNotifyListByType({
        pageNo: 1,
        pageSize: 6,
      });
      const hasNotify = list.some((item) => !item.hasRead);
      this.setSysNotifyFlag(hasNotify);
    },
  },
});

// Need to be used outside the setup
export function useAppStoreWithOut() {
  return useAppStore(store);
}
